import {
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';
import { ICustomDetailListConfiguration } from 'components/controls/CustomDetailList/ICustomDetailListConfiguration';
import { EViewType } from 'global/Application';
import { IDebugOptions } from 'global/Debugger';
import { ArrayToLookup, ArrayToLookupByPredicate } from 'global/Utility/TSFunctions';
import * as IXrmTypes from 'model/CRM';
import { IGLWindow } from 'model/GroupLibraryWindow';

export enum ESnapshotStatus {
  untracked = 1,
  loaded = 2,
}

export enum ELoadStatus {
  idle,
  saving,
  criticalSaving,
  complete
}

export interface IConcurrencyInfo {
  fullname: string,
  modifiedon: string
}

interface IUserConfig extends Record<string, boolean | string | undefined> {
  "defaults.nonstandards.defaultCollapsed"?: boolean;
  "defaults.attributesets.defaultCollapsed"?: boolean;
}

export interface IGroupConfigurationStatus {
  snapshotLoading: boolean;
  dataSavingStatus: ELoadStatus;

  // TODO to retire, use data slice, data.snapshot.account
  account: IXrmTypes.account | null;

  // TODO retire, move to data slice
  snapshotStatus: ESnapshotStatus | null;

  // TODO retire, move to data slice
  snapshotBlobVersion: string | null;

  // TODO retire, move to data slice
  snapshots: IXrmTypes.bcbs_groupconfigurationsnapshot[] | null;
  debug: IDebugOptions;
  displayAll: boolean;

  // TODO review this, we need to move this to data slice
  underwriterRequiredOnly: boolean;
  showTabs: boolean;
  viewType: EViewType | null;
  expandAllPanels: boolean;
  addEditCommentPanel: boolean;
  lastSaved: string | null;
  concurrencyConfirmationModal: IConcurrencyInfo | null;

  // TODO retire this and use userConfigs to allow savin and lloading
  panelStatus: Record<string, boolean>;

  userConfigs: IUserConfig;
  gridConfigs: Record<string, ICustomDetailListConfiguration>;
  processing: boolean;
  creating: boolean;
  workspace: Record<'benefit' | 'plan', string>;
  recycling: boolean;
  highlightFieldId: string | null;
  isNavPinned: boolean;
  webResourceEmbedded: boolean;
  showDeactivated: boolean;
}

/**
 * Returns the default status slice object.
 * @returns The default status slice object.
 */
export const defaultStatusSlice = () => {
  const status: IGroupConfigurationStatus = {
    // this is to only hold data for doing a delta purposes
    snapshotLoading: false,
    creating: false,
    dataSavingStatus: ELoadStatus.idle,
    account: null as (IXrmTypes.account | null),
    snapshotStatus: null as (ESnapshotStatus | null),
    snapshotBlobVersion: null as (string | null),
    snapshots: null as (IXrmTypes.bcbs_groupconfigurationsnapshot[] | null),
    debug: {},
    displayAll: false,
    underwriterRequiredOnly: false,
    showTabs: true,
    viewType: null as (EViewType | null),
    expandAllPanels: false,
    addEditCommentPanel: false,
    lastSaved: null as (string | null),
    concurrencyConfirmationModal: null as IConcurrencyInfo | null,
    panelStatus: {},
    processing: false,
    workspace: {
      'benefit': 'cardSorted',
      'plan': 'cardSorted'
    },
    gridConfigs: {},
    recycling: false,
    highlightFieldId: null,
    isNavPinned: true,
    webResourceEmbedded: false,
    userConfigs: {
      "defaults.nonstandards.collapsed": true,
      "defaults.attributesets.collapsed": true,
    },
    showDeactivated: false,
  }

  const advancedTokens: string[] = [];

  // update configuration from cookie
  const cookieAdvancedTokens = document.cookie.split(';')?.find(t => t.startsWith('advanced'))?.split(',') ?? [];
  advancedTokens.push(...cookieAdvancedTokens);

  // update configuration from url
  const urlParams = new URLSearchParams(window.location.search);
  const urlAdvancedTokens = urlParams.get('advanced');
  if (urlAdvancedTokens) {
    advancedTokens.push(...urlAdvancedTokens.split(','));
  }

  // update debug slice with latest
  status.debug.developer = advancedTokens.includes('developer');
  status.debug.diagnosis = advancedTokens.includes('diagnosis');
  status.debug.admin = advancedTokens.includes('admin');
  status.debug.liveload = advancedTokens.includes('liveload');
  status.debug.preview = advancedTokens.includes('preview');

  if (advancedTokens.includes('cookie'))
    document.cookie = `advanced=${urlAdvancedTokens};path=/`;

  if (advancedTokens.includes('cookieclear'))
    document.cookie = `advanced=;path=/`;

  (window as IGLWindow).debugOptions = status.debug;

  return status;
};

const statusSlice = createSlice({
  name: 'status',
  initialState: defaultStatusSlice() as IGroupConfigurationStatus,
  reducers: {
    purgeOnStatus: (state) => {
      console.debug("[appl] statusSlice.purgeOnStatus")
      return { ...state, ...defaultStatusSlice() };
    },
    updateDataSavingStatus: (state, action: PayloadAction<ELoadStatus>) => {
      state.dataSavingStatus = action.payload;
    },
    changeSnapshotStatus: (state, action: PayloadAction<ESnapshotStatus>) => {
      state.snapshotStatus = action.payload;
    },
    updateCollapse: (state, action: PayloadAction<{ id: string, collapse: boolean }>) => {
      state.panelStatus[action.payload.id] = action.payload.collapse;
    },
    updateCustomDetailListConfiguration: (state, action: PayloadAction<{ id: string, gridConfig: ICustomDetailListConfiguration }>) => {
      state.gridConfigs[action.payload.id] = action.payload.gridConfig;
    },
    updateCreatingStatus: (state, action: PayloadAction<boolean>) => {
      state.creating = action.payload;
    },
    startSnapshotLoad: (state) => {
      state.snapshotLoading = true;
    },
    endSnapshotLoad: (state) => {
      state.snapshotLoading = false;
    },
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.processing = action.payload;
    },
    updateAccount: (state, action: PayloadAction<any>) => {
      console.log('[appl] statusSlice.updateAccount', action.payload);
      state.account = action.payload
    },
    updateSnapshots: (state, action: PayloadAction<any>) => {
      state.snapshots = action.payload
    },
    updateSnapshotBlobVersion: (state, action: PayloadAction<any>) => {
      state.snapshotBlobVersion = action.payload
    },
    updateDebug: (state, action: PayloadAction<Partial<IDebugOptions>>) => {
      state.debug = { ...state.debug, ...action.payload };
      (window as IGLWindow).debugOptions = structuredClone(state.debug);
    },
    setViewType: (state, action: PayloadAction<EViewType | null>) => {
      state.viewType = action.payload;
      console.log('[appl] statusSlice.setViewType', EViewType[state.viewType ?? EViewType.ViewOnly]);
    },
    toggleDisplayAll: (state) => {
      state.displayAll = !state.displayAll;
    },
    toggleUnderwriterRequiredOnly: (state) => {
      state.underwriterRequiredOnly = !state.underwriterRequiredOnly;
    },
    toggleShowTabs: (state) => {
      state.showTabs = !state.showTabs;
    },
    toggleExpandAll: (state) => {
      state.expandAllPanels = !state.expandAllPanels;
    },
    toggleAddEditComment: (state) => {
      state.addEditCommentPanel = !state.addEditCommentPanel;
    },
    updateLastSaved: (state) => {
      state.lastSaved = new Date().toISOString();
    },
    setConcurrencyConfirmationModal: (state, action: (PayloadAction<({ fullname: string, modifiedon: string } | null)>)) => {
      state.concurrencyConfirmationModal = action.payload;
    },
    updateWorkspace: (state, action: PayloadAction<Record<string, string>>) => {
      const t = action.payload;
      state.workspace = { ...state.workspace, ...t };
    },
    setStatusRecycle: (state, action: PayloadAction<boolean>) => {
      state.recycling = action.payload;
    },
    setStatusHighlightField: (state, action: PayloadAction<string>) => {
      state.highlightFieldId = action.payload;
    },
    setIsNavPinned: (state, action: PayloadAction<boolean | undefined>) => {
      if (action.payload === undefined)
        state.isNavPinned = !state.isNavPinned;
      else
        state.isNavPinned = action.payload;
    },
    setWebResourceEmbedded: (state, action: PayloadAction<boolean>) => {
      state.webResourceEmbedded = action.payload;
    },
    setShowDeactivated: (state, action: PayloadAction<boolean>) => {
      state.showDeactivated = action.payload;
    },
  },
})


// Action creators are generated for each case reducer function
export const {
  toggleDisplayAll,
  toggleUnderwriterRequiredOnly,
  toggleShowTabs, toggleExpandAll, toggleAddEditComment,
  changeSnapshotStatus,
  startSnapshotLoad,
  setProcessing,
  updateDataSavingStatus,
  endSnapshotLoad,
  updateAccount,
  updateSnapshotBlobVersion,
  updateSnapshots,
  updateDebug,
  setViewType,
  updateCollapse,
  setConcurrencyConfirmationModal,
  updateLastSaved,
  updateCreatingStatus,
  purgeOnStatus,
  updateWorkspace,
  updateCustomDetailListConfiguration,
  setStatusRecycle,
  setStatusHighlightField,
  setIsNavPinned,
  setWebResourceEmbedded,
  setShowDeactivated,
} = statusSlice.actions


export default statusSlice.reducer